import React from 'react'
import map from 'lodash/fp/map'
import some from 'lodash/fp/some'
import flow from 'lodash/fp/flow'
import trim from 'lodash/fp/trim'
import every from 'lodash/fp/every'
import filter from 'lodash/fp/filter'
import toLower from 'lodash/fp/toLower'
import orderBy from 'lodash/fp/orderBy'

import {
  Guest,
  Booking,
  BookingStatus,
  EventComponent,
  PackageOffering,
  RoomOffering,
} from '../../../../types'
import {
  GridRow,
  GridField,
  SortConfig,
} from '../../../../components/Grid/types'
import { orderPackageAddons } from '../../../../common'

type GridData = (GridRow | GridRow[])[]

export const orderGuests = (
  data: Guest[],
  sortConfig?: SortConfig
): Guest[] => {
  // Only sort by name if key is 'name', otherwise default to asc by name
  const direction = sortConfig?.key === 'name' && sortConfig?.direction
  return orderBy(
    (guest: Guest) => {
      return toLower(`${trim(guest.firstName)} ${trim(guest.lastName)}`)
    },
    direction || 'asc',
    data
  )
}

export const orderData = (
  data: GridData,
  sortConfig?: SortConfig
): GridData => {
  if (!sortConfig) return data
  const { key, direction } = sortConfig

  return orderBy(
    (row: GridRow | GridRow[]) => {
      let resp: GridField
      const isArray = Array.isArray(row) // if it's an array means that the guest has plus ones

      /**
       * Custom sort function for columns with data-position attribute
       * This includes: rooms, event_* columns (dynamic event columns), and flight
       * The data-position attribute indicates:
       * - 0: Booked/Confirmed
       * - 1: Declined
       * - 99: No response/No booking
       */
      const isPositionBasedColumn =
        key === 'rooms' || key === 'flight' || key.startsWith('event_')

      if (isPositionBasedColumn) {
        const getPosition = (r: GridRow): number => {
          const element = r[key]
          if (!element) return 99 // No element means no booking

          // Check if it's a React element with data-position
          const btn = element as React.ReactElement
          if (btn?.props?.['data-position'] !== undefined) {
            return btn.props['data-position']
          }

          // Special handling for event columns without data-position
          // These are CTAbook components (the "+" button for adding RSVP)
          if (key.startsWith('event_')) {
            // Check if it's a CTAbook component (has eventComponent prop)
            if (btn?.props?.eventComponent) {
              return 99 // CTAbook means no booking yet, treat as pending
            }
          }

          // For other elements without data-position (like flight info), treat as booked
          return 0
        }

        // For groups: find the best (lowest) position value in the group
        // This ensures that if anyone in the group is confirmed, the whole group sorts as confirmed
        if (isArray) {
          const positions = row.map((item: GridRow) => getPosition(item))
          return Math.min(...positions) // Return the best (lowest) position
        }

        return getPosition(row)
      }
      /* end of custom sort function for position-based columns */

      if (isArray) {
        row.some((item: GridRow) => {
          const val = item[key]
          if (resp) return true

          resp = typeof val === 'string' ? toLower(val) : val
          return false
        })
        return resp
      }

      const val = row[key]
      return typeof val === 'string' ? toLower(val) : val
    },
    direction,
    data
  )
}

export const searchData = (data: GridData, search?: string): GridData => {
  if (!search) return data
  const searchValue = toLower(search)
  const lower = (str: GridField): string => toLower(String(str || ''))

  const itemMatchesSearch = (item: GridRow): boolean => {
    const name = lower(item?.name)
    const status = lower(item?.status)
    const email = lower(item?.email)
    const rawPhone = lower(item?.rawPhone)

    return (
      name.includes(searchValue) ||
      status.includes(searchValue) ||
      email.includes(searchValue) ||
      rawPhone.includes(searchValue)
    )
  }

  return data?.filter((row: GridRow | GridRow[]) => {
    if (Array.isArray(row)) {
      return row.some(itemMatchesSearch)
    }
    return itemMatchesSearch(row)
  })
}

/**
 * Filter the data by different filters. Right now we are only filtering by event components (packages)
 * @param data: GridRow[] - the data to filter
 * @param packages: { byPackage: string[], byStatus: string[] } - the filters for packages - both are required
 * @param rooms: string[] - the filters for rooms
 * @param type: PackageOffering['__typename'] | RoomOffering['__typename']
 * @returns GridRow[] - the filtered data
 */
export const filterData = ({
  data,
  packages,
  rooms = [],
  type,
}: {
  // packages: { byPackage: filterPackages, byStatus: filterByEventStatus },
  data: GridRow[]
  packages?: {
    byPackage: string[]
    byStatus: string[]
  }
  rooms?: string[]
  type: PackageOffering['__typename'] | RoomOffering['__typename']
}): GridRow[] => {
  if (!type) return data

  /**
   * Combines all booking data from dynamic event columns (event_*) into a single array
   * Each event column contains data-bookings attribute with booking status info
   */
  const getAllBookings = (r: GridRow): string => {
    const eventKeys = Object.keys(r).filter(key => key.startsWith('event_'))
    const allBookings = eventKeys.reduce(
      (acc: Array<{ component: string; status: string }>, key) => {
        const btn = r[key] as React.ReactElement
        const bookingsStr = btn?.props?.['data-bookings']
        if (bookingsStr) {
          try {
            const bookings = JSON.parse(bookingsStr)
            return [...acc, ...bookings]
          } catch (e) {
            return acc
          }
        }
        return acc
      },
      []
    )
    return JSON.stringify(allBookings)
  }

  if (type === 'RoomOffering') {
    // right now there is no filter for RoomOffering so this is just to leave the structure
    // in case we need to add a filter for rooms in the future
    if (rooms?.length <= 0) return data
    return data
  }

  // type === 'PackageOffering'
  if (!packages) return data
  const { byPackage, byStatus } = packages
  if (byPackage.length <= 0 && byStatus.length <= 0) return data // both filters are required

  return filter(row => {
    const bookings: {
      component: string
      status: string
    }[] = JSON.parse(getAllBookings(row) || '[]')

    return every(packageId => {
      const matchingBookingsByEC = filter(
        b => b.component === packageId,
        bookings
      )
      if (matchingBookingsByEC.length === 0) return false

      if (byStatus.length > 0) {
        return some(
          booking => byStatus.includes(booking.status),
          matchingBookingsByEC
        )
      }
      return true
    }, byPackage)
  }, data)
}

export const bookingStatusAux = (
  booking: Booking,
  className = ''
): React.ReactElement => {
  const { Declined, Cancelled, Booked } = BookingStatus
  const { status } = booking
  if (status === Declined || status === Cancelled) {
    return <span className={className || 'd-block pt-2'}>Declined</span>
  }
  if (status === Booked) {
    return <span className={className || 'd-block'}>Attending</span>
  }
  return <span />
}

/**
 * Sort the packages by event component date and the first addon time
 * @param components EventComponent[]
 * @returns EventComponent[]
 */
export const sortPackages = (
  components: EventComponent[] = []
): EventComponent[] => {
  if (components.length <= 1) return components

  // order first by time and then by the first addon time
  return flow(
    map((el: EventComponent) => ({
      ...el,
      packageCustomization: orderPackageAddons(el.packageCustomization),
      parsedDate: new Date(el.date), // there were some issues with the date format so we are parsing it here
    })),
    orderBy(['parsedDate', 'packageCustomization[0].time'], ['asc', 'asc'])
  )(components) as EventComponent[]
}
